/**
 * Боковая панель навигации админ-панели
 * Содержит логотип, навигационные ссылки и ссылку на TV-экран
 */

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

/** Элемент навигации */
interface NavItem {
  to: string;
  label: string;
  icon: string;
  external?: boolean;
}

const navItems: NavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: '📊' },
  { to: '/admin/schedule', label: 'Расписание', icon: '📅' },
  { to: '/admin/routes', label: 'Маршруты', icon: '🚌' },
  { to: '/admin/buses', label: 'Автобусы', icon: '🚍' },
];

interface SidebarProps {
  /** Ширина sidebar при скрытом состоянии */
  collapsed?: boolean;
  /** Переключить состояние */
  onToggle?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggle }) => {
  const location = useLocation();

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-slate-900 text-white flex flex-col transition-all duration-300 z-40 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Логотип */}
      <div className="flex items-center h-16 px-4 border-b border-slate-700">
        <span className="text-2xl" role="img" aria-label="bus">🚌</span>
        {!collapsed && (
          <span className="ml-3 font-bold text-lg tracking-tight truncate">
            Bus Admin
          </span>
        )}
      </div>

      {/* Навигация */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive =
            item.to === '/admin'
              ? location.pathname === '/admin'
              : location.pathname.startsWith(item.to);

          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/admin'}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                isActive
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/30'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
              title={collapsed ? item.label : undefined}
            >
              <span className="text-xl flex-shrink-0">{item.icon}</span>
              {!collapsed && (
                <span className="font-medium text-sm">{item.label}</span>
              )}
              {/* Индикатор активного пункта (точка при collapsed) */}
              {isActive && collapsed && (
                <span className="absolute right-2 w-1.5 h-1.5 bg-blue-400 rounded-full" />
              )}
            </NavLink>
          );
        })}

        {/* Разделитель */}
        <div className="my-3 border-t border-slate-700" />

        {/* Ссылка на TV-экран */}
        <a
          href="/display"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all duration-200"
          title={collapsed ? 'TV Экран' : undefined}
        >
          <span className="text-xl flex-shrink-0">📺</span>
          {!collapsed && (
            <span className="font-medium text-sm">TV Экран</span>
          )}
          {!collapsed && (
            <svg
              className="w-3.5 h-3.5 ml-auto text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          )}
        </a>
      </nav>

      {/* Кнопка свернуть/развернуть */}
      <div className="p-2 border-t border-slate-700">
        <button
          onClick={onToggle}
          className="flex items-center justify-center w-full py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          title={collapsed ? 'Развернуть' : 'Свернуть'}
        >
          <svg
            className={`w-5 h-5 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
          </svg>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
